import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/app/layouts';
import { AuthGuard } from './guards';
import { LoadingState } from '@/components/feedback';
import { APP_ROUTES } from '@/config/constants';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const BudgetsPage = lazy(() => import('@/features/budgets/pages/BudgetsPage'));
const PartiesPage = lazy(() => import('@/features/parties/pages/PartiesPage'));
const PaymentJustificationsPage = lazy(
  () => import('@/features/payment-justifications/pages/PaymentJustificationsPage')
);
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const GeneralSettingsPage = lazy(() => import('@/features/settings/pages/GeneralSettingsPage'));
const MembersSettingsPage = lazy(() => import('@/features/settings/pages/MembersSettingsPage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const PersonalInfoPage = lazy(() => import('@/features/profile/pages/PersonalInfoPage'));
const SignInPage = lazy(() => import('@/features/auth/pages/SignInPage'));
const NotFoundPage = lazy(() => import('@/features/auth/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/features/auth/pages/ForbiddenPage'));

// Suspense wrapper for lazy loaded components
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Admin routes (protected)
  {
    path: '/',
    element: (
      <AuthGuard>
        <AdminLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyPage>
            <DashboardPage />
          </LazyPage>
        ),
      },
      {
        path: 'partidas-presupuestarias',
        element: (
          <LazyPage>
            <BudgetsPage />
          </LazyPage>
        ),
      },
      {
        path: 'terceros',
        element: (
          <LazyPage>
            <PartiesPage />
          </LazyPage>
        ),
      },
      {
        path: 'justificaciones-pago',
        element: (
          <LazyPage>
            <PaymentJustificationsPage />
          </LazyPage>
        ),
      },
      {
        path: 'configuraciones',
        element: (
          <LazyPage>
            <SettingsPage />
          </LazyPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="general" replace />,
          },
          {
            path: 'general',
            element: (
              <LazyPage>
                <GeneralSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'miembros',
            element: (
              <LazyPage>
                <MembersSettingsPage />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'perfil',
        element: (
          <LazyPage>
            <ProfilePage />
          </LazyPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="datos-personales" replace />,
          },
          {
            path: 'datos-personales',
            element: (
              <LazyPage>
                <PersonalInfoPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },

  // Public routes
  {
    path: APP_ROUTES.SIGNIN,
    element: (
      <LazyPage>
        <SignInPage />
      </LazyPage>
    ),
  },

  // 403 - Forbidden
  {
    path: APP_ROUTES.FORBIDDEN,
    element: (
      <LazyPage>
        <ForbiddenPage />
      </LazyPage>
    ),
  },

  // 404 - Not Found
  {
    path: APP_ROUTES.NOT_FOUND,
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },

  // Catch all - redirect to 404
  {
    path: '*',
    element: <Navigate to={APP_ROUTES.NOT_FOUND} replace />,
  },
]);
